import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ROLE_LABELS, ROLE_COLORS, MODULE_LABELS, MODULES } from "../lib/constants";
import { toast } from "sonner";
import { UserPlus, Trash2, KeyRound, X, ShieldCheck, SlidersHorizontal, Activity, Gauge, Eye, Pencil, Crown } from "lucide-react";
import { useSettings } from "../context/SettingsContext";
import { humanDur } from "../components/Timeline";

const TEAM_ROLES = ["compra", "picker", "accettazione", "amministrazione", "autista"];
const fmt = (iso) => { if (!iso) return "mai"; try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };

export default function Utenti() {
  const { text } = useSettings();
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [perf, setPerf] = useState([]);
  const [form, setForm] = useState({ name: "", username: "", password: "", role: "picker" });
  const [pwUser, setPwUser] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [permUser, setPermUser] = useState(null);
  const [permState, setPermState] = useState({});
  const [superState, setSuperState] = useState(false);
  const [activityUser, setActivityUser] = useState(null);
  const [logs, setLogs] = useState([]);

  const load = async () => {
    const [u, p] = await Promise.all([api.get("/users"), api.get("/team/performance").catch(() => ({ data: [] }))]);
    setUsers(u.data);
    setPerf(p.data);
  };
  useEffect(() => { load().catch(() => {}); const t = setInterval(() => load().catch(() => {}), 20000); return () => clearInterval(t); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name || !form.username || !form.password) { toast.error("Compila tutti i campi"); return; }
    try { await api.post("/users", form); toast.success("Utente creato"); setForm({ name: "", username: "", password: "", role: "picker" }); load(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const remove = async (id) => { try { await api.delete(`/users/${id}`); toast.success("Utente eliminato"); load(); } catch (err) { toast.error(apiError(err)); } };
  const savePassword = async () => {
    if (!newPw || newPw.length < 4) { toast.error("Password troppo corta (min 4)"); return; }
    try { await api.put(`/users/${pwUser.id}/password`, { password: newPw }); toast.success(`Password aggiornata per ${pwUser.name}`); setPwUser(null); setNewPw(""); }
    catch (err) { toast.error(apiError(err)); }
  };

  const openPerms = (u) => {
    setPermUser(u);
    setSuperState(!!u.is_superadmin);
    const eff = u.permissions_effective || {};
    setPermState(Object.fromEntries(MODULES.map((m) => [m, { view: !!eff[m]?.view, manage: !!eff[m]?.manage }])));
  };
  const togglePerm = (m, action) => setPermState((s) => {
    const cur = { ...s[m], [action]: !s[m][action] };
    if (action === "manage" && cur.manage) cur.view = true;
    if (action === "view" && !cur.view) cur.manage = false;
    return { ...s, [m]: cur };
  });
  const savePerms = async () => {
    try { await api.put(`/users/${permUser.id}/permissions`, { is_superadmin: superState, permissions: permState }); toast.success("Permessi aggiornati"); setPermUser(null); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const openActivity = async (u) => {
    setActivityUser(u); setLogs([]);
    try { const { data } = await api.get("/activity", { params: { user_id: u.id, limit: 100 } }); setLogs(data); } catch { setLogs([]); }
  };

  const perfOf = (id) => perf.find((p) => p.user_id === id);
  const input = "w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100 outline-none focus:border-primary";

  return (
    <div className="animate-fade-up">
      <h1 className="font-display font-black text-4xl text-slate-50 tracking-tight mb-1">{text("utenti_title")}</h1>
      <p className="text-slate-400 mb-6">{text("utenti_subtitle")}</p>

      <form onSubmit={create} data-testid="user-form" className="bg-slate-900/50 border border-slate-800 rounded-md p-5 mb-8 grid grid-cols-1 md:grid-cols-5 gap-3">
        <input data-testid="user-name" placeholder="Nome" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={input} />
        <input data-testid="user-username" placeholder="Nickname (login)" autoCapitalize="none" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className={input} />
        <input data-testid="user-password" type="text" placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={input} />
        <select data-testid="user-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={input}>
          {TEAM_ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
        </select>
        <button data-testid="user-submit" type="submit" className="bg-primary text-slate-950 font-semibold rounded-md px-4 py-2 flex items-center justify-center gap-2 hover:opacity-90 transition-opacity">
          <UserPlus className="w-4 h-4" /> Crea
        </button>
      </form>

      <div className="space-y-2" data-testid="users-list">
        {users.map((u) => {
          const pf = perfOf(u.id);
          return (
            <div key={u.id} data-testid={`user-${u.id}`} className="bg-slate-900/50 border border-slate-800 rounded-md p-4">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center font-bold text-slate-200 uppercase">{u.name?.[0]}</div>
                  <span data-testid={`online-${u.id}`} className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-slate-900 ${u.online ? "bg-emerald-400" : "bg-slate-600"}`} title={u.online ? "Online" : "Offline"} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-slate-100">{u.name}</span>
                    {u.is_superadmin && <span data-testid={`super-badge-${u.id}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border bg-amber-500/15 text-amber-300 border-amber-500/30"><Crown className="w-3 h-3" /> Super Admin</span>}
                  </div>
                  <div className="text-xs text-slate-500 font-mono-plex">@{u.username} · {u.online ? <span className="text-emerald-400">online ora</span> : `ultimo accesso ${fmt(u.last_seen || u.last_login)}`}</div>
                </div>
                <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded border ${ROLE_COLORS[u.role]}`}>{ROLE_LABELS[u.role]}</span>
                {u.role !== "admin" && (
                  <button data-testid={`perms-user-${u.id}`} onClick={() => openPerms(u)} className="text-slate-500 hover:text-primary" title="Permessi"><SlidersHorizontal className="w-4 h-4" /></button>
                )}
                <button data-testid={`activity-user-${u.id}`} onClick={() => openActivity(u)} className="text-slate-500 hover:text-primary" title="Attività"><Activity className="w-4 h-4" /></button>
                <button data-testid={`pw-user-${u.id}`} onClick={() => { setPwUser(u); setNewPw(""); }} className="text-slate-500 hover:text-primary" title="Cambia password"><KeyRound className="w-4 h-4" /></button>
                {u.id !== user.id && u.role !== "admin" && (
                  <button data-testid={`delete-user-${u.id}`} onClick={() => remove(u.id)} className="text-slate-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                )}
              </div>
              {pf && (pf.ritiri_completati > 0 || pf.accettazioni > 0 || pf.bolle_registrate > 0 || pf.acquisti_creati > 0) && (
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 pt-3 border-t border-slate-800 text-xs" data-testid={`perf-${u.id}`}>
                  <span className="flex items-center gap-1 text-slate-400"><Gauge className="w-3.5 h-3.5 text-primary" /> Performance:</span>
                  {pf.ritiri_completati > 0 && <span className="text-slate-300">Ritiri: <b>{pf.ritiri_completati}</b></span>}
                  {pf.tempo_medio_reazione_sec != null && <span className="text-slate-400">Reazione media: <b className="text-slate-200">{humanDur(pf.tempo_medio_reazione_sec)}</b></span>}
                  {pf.tempo_medio_completamento_sec != null && <span className="text-slate-400">Completamento: <b className="text-slate-200">{humanDur(pf.tempo_medio_completamento_sec)}</b></span>}
                  {pf.accettazioni > 0 && <span className="text-slate-300">Accettazioni: <b>{pf.accettazioni}</b></span>}
                  {pf.tempo_medio_accettazione_sec != null && <span className="text-slate-400">Tempo controllo: <b className="text-slate-200">{humanDur(pf.tempo_medio_accettazione_sec)}</b></span>}
                  {pf.bolle_registrate > 0 && <span className="text-slate-300">Bolle: <b>{pf.bolle_registrate}</b></span>}
                  {pf.acquisti_creati > 0 && <span className="text-slate-300">Acquisti: <b>{pf.acquisti_creati}</b></span>}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Modal permessi */}
      {permUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4" onClick={() => setPermUser(null)}>
          <div data-testid="perms-modal" className="bg-slate-900 border border-slate-700 rounded-lg w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-slate-900 border-b border-slate-800 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-primary" /><h3 className="font-display font-black text-xl text-slate-50">Permessi · {permUser.name}</h3></div>
              <button onClick={() => setPermUser(null)} className="text-slate-400 hover:text-slate-100"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6">
              <label className="flex items-center justify-between gap-3 bg-amber-500/10 border border-amber-500/30 rounded-md px-4 py-3 mb-5 cursor-pointer">
                <div className="flex items-center gap-2"><Crown className="w-5 h-5 text-amber-400" /><div><div className="text-sm font-semibold text-amber-200">Super Admin</div><div className="text-xs text-amber-300/70">Accesso totale come te (fa e vede tutto)</div></div></div>
                <input data-testid="perm-superadmin" type="checkbox" checked={superState} onChange={(e) => setSuperState(e.target.checked)} className="w-5 h-5 accent-amber-400" />
              </label>
              <div className={`space-y-1.5 ${superState ? "opacity-40 pointer-events-none" : ""}`}>
                <div className="grid grid-cols-[1fr_auto_auto] gap-3 px-2 text-[10px] uppercase tracking-wider text-slate-500">
                  <span>Modulo</span><span className="w-14 text-center">Vede</span><span className="w-16 text-center">Modifica</span>
                </div>
                {MODULES.map((m) => (
                  <div key={m} className="grid grid-cols-[1fr_auto_auto] gap-3 items-center bg-slate-800/50 rounded-md px-3 py-2">
                    <span className="text-sm text-slate-200">{MODULE_LABELS[m]}</span>
                    <label className="w-14 flex justify-center cursor-pointer"><input data-testid={`perm-${m}-view`} type="checkbox" checked={permState[m]?.view || false} onChange={() => togglePerm(m, "view")} className="w-4 h-4 accent-primary" /></label>
                    <label className="w-16 flex justify-center cursor-pointer"><input data-testid={`perm-${m}-manage`} type="checkbox" checked={permState[m]?.manage || false} onChange={() => togglePerm(m, "manage")} className="w-4 h-4 accent-emerald-400" /></label>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-3 mt-4 text-[11px] text-slate-500">
                <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> Vede = accede al modulo</span>
                <span className="flex items-center gap-1"><Pencil className="w-3 h-3" /> Modifica = crea/aggiorna/elimina</span>
              </div>
              <button data-testid="perms-save-btn" onClick={savePerms} className="w-full mt-5 bg-primary text-slate-950 font-semibold rounded-md py-2.5 hover:opacity-90 transition-opacity">Salva permessi</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal attività */}
      {activityUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4" onClick={() => setActivityUser(null)}>
          <div data-testid="activity-modal" className="bg-slate-900 border border-slate-700 rounded-lg w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-slate-900 border-b border-slate-800 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2"><Activity className="w-5 h-5 text-primary" /><h3 className="font-display font-black text-xl text-slate-50">Attività · {activityUser.name}</h3></div>
              <button onClick={() => setActivityUser(null)} className="text-slate-400 hover:text-slate-100"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-2">
              {logs.length === 0 && <p className="text-slate-500 text-sm">Nessuna attività registrata.</p>}
              {logs.map((l) => (
                <div key={l.id} className="flex items-center justify-between gap-3 text-sm border-b border-slate-800 pb-1.5">
                  <span className="text-slate-200">{(l.action || "").replace(/_/g, " ")}{l.detail ? <span className="text-slate-500"> · {l.detail}</span> : ""}</span>
                  <span className="text-xs font-mono-plex text-slate-500 shrink-0">{fmt(l.ts)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {pwUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setPwUser(null)}>
          <div data-testid="pw-modal" className="bg-slate-900 border border-slate-800 rounded-md p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-black text-xl text-slate-50">Nuova password</h3>
              <button onClick={() => setPwUser(null)} className="text-slate-400 hover:text-slate-100"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-slate-400 mb-3">Imposta una nuova password per <span className="text-slate-200 font-semibold">{pwUser.name}</span> (@{pwUser.username}).</p>
            <input data-testid="pw-input" type="text" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="Nuova password" className={`${input} mb-4`} />
            <button data-testid="pw-save-btn" onClick={savePassword} className="w-full bg-primary text-slate-950 font-semibold rounded-md py-2.5 hover:opacity-90 transition-opacity">Salva password</button>
          </div>
        </div>
      )}
    </div>
  );
}
